/**
 * Dashboard.jsx
 *
 * Analytics overview for the AE Tracker.
 * KPI cards + charts by status, clinic, severity, treatment, and time trend.
 * Data is derived client-side from a slim getCases({ summary: true }) fetch
 * (chart fields only — no per-case tags/costs rollups).
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, LineChart, Line, PieChart, Pie, Cell, Legend, Brush,
} from 'recharts';
import { getCases, getCostsByCase } from '../api/client.js';
import {
  DASHBOARD_DEFAULT_PRESET,
  defaultDashboardRange,
  dashboardSinceIso,
} from '../lib/caseLoadPolicy.js';
import { getTherapieMapsData } from '../api/blobClient.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { useAppConfig } from '../contexts/AppConfigContext.jsx';
import { classColor, colorForConfigItem } from '../utils/badgeColors.js';
import StatCard from '../components/StatCard.jsx';
import KpiGrid from '../components/KpiGrid.jsx';
import SearchableSelect from '../components/SearchableSelect.jsx';
import DateInput from '../components/DateInput.jsx';
import ShareLinkButton from '../components/ShareLinkButton.jsx';
import useUrlViewSync from '../hooks/useUrlViewSync.js';
import {
  parseDashboardParams,
  buildDashboardParams,
  dashboardRangeFromParams,
} from '../utils/urlState.js';

// ── Colour palette (mirrors styles/global.css :root tokens — recharts needs
//    literal values, so keep these in sync with the stylesheet) ─────────────────
const C_BRAND  = '#0D9488'; // --brand
const C_INFO   = '#2563EB'; // --info
const C_WARN   = '#D97706'; // --warn
const C_DANGER = '#DC2626'; // --danger
const C_OK     = '#059669'; // --ok
const C_VIOLET = '#7C3AED'; // --violet
const C_MUTED  = '#8A94A6'; // --text-3
const C_ORANGE = '#EA580C'; // chart-strength orange — sits between --warn and --danger in the severity ramp

// Status & severity chart colours are no longer hardcoded here — they're pulled
// from each config item's cssClass via badgeColors.classColor(), so charts stay
// linked to the badges/pills. See colorForConfigItem usage below.

// Normalised country names (see normaliseCountry() in client.js)
const COUNTRY_COLOURS = {
  'Ireland':        C_OK,
  'United Kingdom': C_INFO,
  'United States':  C_DANGER,
};

// Short display labels — match the New Case wizard + Cases country filter.
const COUNTRY_LABELS = {
  'United Kingdom': 'GB',
  'Ireland':        'IOI',
};

// ── Helpers ────────────────────────────────────────────────────────────────────

function countBy(arr, key) {
  const map = {};
  for (const item of arr) {
    const val = item[key] || 'Unknown';
    map[val] = (map[val] || 0) + 1;
  }
  return Object.entries(map)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
}

function countByField(arr, accessor) {
  const map = {};
  for (const item of arr) {
    const val = accessor(item) || 'Unknown';
    map[val] = (map[val] || 0) + 1;
  }
  return Object.entries(map)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
}

const COUNTRY_SORT_ORDER = ['United Kingdom', 'Ireland', 'United States'];

/** Dominant country per region from clinic + case data. */
function regionCountryMap(clinics, cases) {
  const counts = {};
  const add = (region, country) => {
    const r = (region || '').trim();
    const c = (country || '').trim();
    if (!r || r === 'Unknown' || !c || c === 'Unknown') return;
    if (!counts[r]) counts[r] = {};
    counts[r][c] = (counts[r][c] || 0) + 1;
  };
  for (const c of clinics) add(c.region, c.country);
  for (const c of cases) add(c.region, c.country);

  const out = {};
  for (const [region, byCountry] of Object.entries(counts)) {
    out[region] = Object.entries(byCountry).sort((a, b) => b[1] - a[1])[0][0];
  }
  return out;
}

function compareCountries(a, b) {
  const ia = COUNTRY_SORT_ORDER.indexOf(a);
  const ib = COUNTRY_SORT_ORDER.indexOf(b);
  const oa = ia >= 0 ? ia : 99;
  const ob = ib >= 0 ? ib : 99;
  if (oa !== ob) return oa - ob;
  return (a || '').localeCompare(b || '');
}

/** Scope picker — countries present in clinic + case data. */
function buildCountrySelectOptions(clinics, cases) {
  const set = new Set();
  const add = (country) => {
    const c = (country || '').trim();
    if (c && c !== 'Unknown') set.add(c);
  };
  for (const c of clinics) add(c.country);
  for (const c of cases) add(c.country);
  return [...set]
    .sort(compareCountries)
    .map(country => ({
      value: country,
      label: COUNTRY_LABELS[country] || country,
    }));
}

/** Scope picker — regions, optionally limited to one country. */
function buildRegionSelectOptions(clinics, cases, countryFilter = null) {
  const countryByRegion = regionCountryMap(clinics, cases);
  const regions = new Set();
  const add = (region, country) => {
    const r = (region || '').trim();
    const c = (country || countryByRegion[r] || '').trim();
    if (!r || r === 'Unknown') return;
    if (countryFilter && c !== countryFilter) return;
    regions.add(r);
  };
  for (const c of clinics) add(c.region, c.country);
  for (const c of cases) add(c.region, c.country);

  return [...regions]
    .sort((a, b) => a.localeCompare(b))
    .map(region => ({ value: region, label: region }));
}

/** Region counts nested under country — sorted by country, then region name. */
function countRegionsByCountry(arr) {
  const map = {};
  for (const c of arr) {
    const country = (c.country || '').trim();
    const region  = (c.region  || '').trim();
    if (!country || country === 'Unknown' || !region || region === 'Unknown') continue;
    if (!map[country]) map[country] = {};
    map[country][region] = (map[country][region] || 0) + 1;
  }

  return Object.keys(map)
    .sort((a, b) => {
      const ia = COUNTRY_SORT_ORDER.indexOf(a);
      const ib = COUNTRY_SORT_ORDER.indexOf(b);
      const oa = ia >= 0 ? ia : 99;
      const ob = ib >= 0 ? ib : 99;
      if (oa !== ob) return oa - ob;
      return a.localeCompare(b);
    })
    .map(country => {
      const regions = Object.entries(map[country])
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([name, count]) => ({ name, count }));
      return {
        country,
        countryLabel: COUNTRY_LABELS[country] || country,
        color:        COUNTRY_COLOURS[country] || C_MUTED,
        regions,
        total: regions.reduce((s, r) => s + r.count, 0),
      };
    });
}

/** Group cases by calendar month (YYYY-MM) and return sorted array. */
function countByMonth(arr) {
  const map = {};
  for (const item of arr) {
    const dt = item.reportedDate || item.createdDate;
    if (!dt) continue;
    const d = new Date(dt);
    if (isNaN(d)) continue;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    map[key] = (map[key] || 0) + 1;
  }
  return Object.entries(map)
    .map(([month, count]) => ({ month, count }))
    .sort((a, b) => a.month.localeCompare(b.month));
}

/**
 * Like countByMonth, but splits into three event-type buckets via the
 * `eventType` field (migration 007's cases.event_type discriminator — the old
 * department/treatment-based discriminator was cleared by that migration):
 *   - 'NearMiss'       → nearMiss
 *   - 'ClinicIncident' → clinicIncident
 *   - anything else    → treatment
 */
function countByMonthSeries(arr) {
  const map = {};
  for (const item of arr) {
    const dt = item.reportedDate || item.createdDate;
    if (!dt) continue;
    const d = new Date(dt);
    if (isNaN(d)) continue;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    if (!map[key]) map[key] = { month: key, treatment: 0, nearMiss: 0, clinicIncident: 0 };
    const t = item.eventType;
    if (t === 'NearMiss') map[key].nearMiss += 1;
    else if (t === 'ClinicIncident') map[key].clinicIncident += 1;
    else map[key].treatment += 1;
  }
  return Object.values(map).sort((a, b) => a.month.localeCompare(b.month));
}

/** Format YYYY-MM as "Jan 24" style label */
function fmtMonth(m) {
  const [y, mo] = m.split('-');
  const d = new Date(Number(y), Number(mo) - 1, 1);
  return d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
}

function staffName(c) {
  return (c._raw?.StaffMemberConcerned || '').trim();
}

/** Per-staff case counts within the filtered set (offence = report count). */
function computeStaffOffences(arr) {
  const map = {};
  for (const c of arr) {
    const name = staffName(c);
    if (!name) continue;
    if (!map[name]) map[name] = { name, count: 0, latest: null };
    map[name].count += 1;
    const dt = c.reportedDate || c.createdDate;
    if (dt) {
      const d = new Date(dt);
      if (!isNaN(d) && (!map[name].latest || d > map[name].latest)) map[name].latest = d;
    }
  }
  const staff = Object.values(map).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  return {
    staff,
    first:  staff.filter(s => s.count === 1).length,
    second: staff.filter(s => s.count === 2).length,
    third:  staff.filter(s => s.count >= 3).length,
    unnamed: arr.filter(c => !staffName(c)).length,
  };
}

/** Count cases reported in the current calendar month */
function countThisMonth(arr) {
  const now = new Date();
  return arr.filter(c => {
    const dt = c.reportedDate || c.createdDate;
    if (!dt) return false;
    const d = new Date(dt);
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  }).length;
}

// ── Timeline range selector ───────────────────────────────────────────────────

const WEEK_MS = 7 * 864e5;

const PRESETS = [
  { key: '30',     label: '30d',       days: 30   },
  { key: '60',     label: '60d',       days: 60   },
  { key: 'year',   label: 'This year', year: true },
  { key: 'all',    label: 'All',       days: null },
  { key: 'custom', label: 'Custom',    days: null, custom: true },
];

const fmtDay = d => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' });

/** Weekly case-count bins spanning the full data range (drives the timeline brush). */
function buildTimelineBins(arr) {
  const dates = [];
  for (const c of arr) {
    const d = new Date(c.reportedDate || c.createdDate);
    if (!isNaN(d)) dates.push(d);
  }
  if (!dates.length) return [];
  const lo = new Date(Math.min(...dates));
  lo.setHours(0, 0, 0, 0);
  lo.setDate(lo.getDate() - (lo.getDay() + 6) % 7); // back to Monday
  const hi = Math.max(...dates);
  const bins = [];
  for (let t = lo.getTime(); t <= hi; t += WEEK_MS) {
    const start = new Date(t);
    bins.push({ start, count: 0, label: fmtDay(start) });
  }
  for (const d of dates) {
    const i = Math.floor((d - lo) / WEEK_MS);
    if (bins[i]) bins[i].count++;
  }
  return bins;
}

/** Map a date range onto brush bin indices (full span when range is null). */
function rangeToIndices(bins, range) {
  if (!range || !bins.length) return [0, Math.max(bins.length - 1, 0)];
  let s = bins.findIndex(b => b.start.getTime() + WEEK_MS > range.start.getTime());
  if (s < 0) s = bins.length - 1;
  let e = 0;
  for (let i = bins.length - 1; i >= 0; i--) {
    if (bins[i].start <= range.end) { e = i; break; }
  }
  return [s, Math.max(e, s)];
}

// ── Brush traveller (custom handle) ────────────────────────────────────────────
// Replaces Recharts' default pill-shaped traveller with a circular knob that
// matches the design system. Renders inside the brush SVG; the shadow comes
// from CSS (filter: drop-shadow) so we don't have to declare an SVG filter.

function BrushTraveller(props) {
  const { x, y, width, height } = props;
  const cx = x + width / 2;
  const cy = y + height / 2;
  return (
    <g className="brush-traveller-knob">
      <circle cx={cx} cy={cy} r={7} fill="var(--brand)" />
    </g>
  );
}

// ── Chart tooltip wrapper ──────────────────────────────────────────────────────
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'var(--surface-2)',
      border: '1px solid var(--border)',
      borderRadius: 8,
      padding: '8px 12px',
      fontSize: 13,
      color: 'var(--text)',
      boxShadow: 'var(--shadow-md)',
    }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color || 'var(--text)' }}>
          {p.name ? `${p.name}: ` : ''}<strong>{p.value}</strong>
        </div>
      ))}
    </div>
  );
}

// ── Custom date-range row ─────────────────────────────────────────────────────
// Two native date inputs; applies live as the user changes them. Defaults pull
// from the active range (so toggling Custom keeps the current window) or from
// the full timeline data span when there is no range yet.

function CustomRangeRow({ range, bins, onApply, onClear }) {
  const toIso = d => (d instanceof Date ? d.toISOString().slice(0, 10) : '');
  const defaultFrom = range?.start
    ? toIso(range.start)
    : (bins[0]?.start ? toIso(bins[0].start) : '');
  const defaultTo = range?.end
    ? toIso(range.end)
    : (bins.length ? toIso(bins[bins.length - 1].start) : '');

  const [from, setFrom] = useState(defaultFrom);
  const [to,   setTo]   = useState(defaultTo);

  // Keep inputs in sync when range is changed externally (preset click etc.)
  useEffect(() => { setFrom(defaultFrom); }, [defaultFrom]);
  useEffect(() => { setTo(defaultTo);     }, [defaultTo]);

  const labelStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontFamily: 'inherit',
    fontSize: 'var(--font-base)',
    color: 'var(--text-2)',
  };
  const inputStyle = {
    padding: '4px 8px',
    fontFamily: 'inherit',
    fontSize: 'var(--font-base)',
    color: 'var(--text)',
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 6,
  };

  // Apply is only enabled with a valid, non-inverted range.
  const canApply = !!from && !!to && new Date(from) <= new Date(to);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 8,
        padding: '8px 16px',
        borderTop: '1px solid var(--border)',
        background: 'var(--surface-2)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <label style={labelStyle}>
          From
          <DateInput
            className=""
            value={from}
            max={to || undefined}
            onChange={e => setFrom(e.target.value)}
            style={{ ...inputStyle, width: 128 }}
          />
        </label>
        <label style={labelStyle}>
          To
          <DateInput
            className=""
            value={to}
            min={from || undefined}
            onChange={e => setTo(e.target.value)}
            style={{ ...inputStyle, width: 128 }}
          />
        </label>
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <button className="btn ghost sm" onClick={onClear}>Clear</button>
        <button
          className="btn primary sm"
          onClick={() => canApply && onApply(from, to)}
          disabled={!canApply}
        >
          Apply
        </button>
      </div>
    </div>
  );
}

// ── Cost section ───────────────────────────────────────────────────────────────
// One KPI tile per currency in use, plus a per-category breakdown chart with a
// currency toggle when more than one currency is present. Spec: no FX, totals
// never cross currencies.


function formatMoney(amount, currency) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency', currency, maximumFractionDigits: 0,
    }).format(Number(amount) || 0);
  } catch {
    return `${currency} ${Math.round(Number(amount) || 0).toLocaleString()}`;
  }
}

function CostSection({ costRows, visibleCaseIds, categories, currencies, activeCurrency, onSelectCurrency }) {
  // Aggregate visible cost rows into { currency: { total, byCategory } }.
  // This recomputes whenever the timeline brush moves visibleCaseIds.
  const byCurrency = {};
  for (const r of costRows) {
    if (!visibleCaseIds.has(r.caseId)) continue;
    const cur = r.currency;
    if (!byCurrency[cur]) byCurrency[cur] = { total: 0, byCategory: {} };
    byCurrency[cur].total += r.total;
    byCurrency[cur].byCategory[r.categoryKey] =
      (byCurrency[cur].byCategory[r.categoryKey] || 0) + r.total;
  }

  const codes = Object.keys(byCurrency).sort((a, b) => byCurrency[b].total - byCurrency[a].total);
  if (!codes.length) return null;

  const labelFor  = key  => categories.find(c => c.key === key)?.label   || key;
  const symbolFor = code => currencies.find(c => c.code === code)?.symbol || '';

  const cur = activeCurrency && byCurrency[activeCurrency] ? activeCurrency : codes[0];
  const byCategory = byCurrency[cur]?.byCategory || {};

  // Bar colour = the category's pill colour (via its cssClass), so the chart and
  // the cost-category pills always match and stay linked through the admin config.
  const barData = Object.entries(byCategory)
    .map(([key, value]) => ({
      name:  labelFor(key),
      value: Number(value) || 0,
      color: colorForConfigItem(categories, 'key', key),
    }))
    .filter(d => d.value > 0)
    .sort((a, b) => b.value - a.value);

  // Chart height drives panel height; KPI column matches via stretch.
  const chartHeight = Math.max(220, barData.length * 36 + 40);

  return (
    <div className="reveal delay-1" style={{ marginBottom: 16 }}>
      <div className="panel">
        <div className="panel-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div className="panel-title">Costs · by category</div>
          {codes.length > 1 && (
            <div style={{ display: 'flex', gap: 4 }}>
              {codes.map(code => (
                <button
                  key={code}
                  className={`btn sm ${cur === code ? 'primary' : 'ghost'}`}
                  onClick={() => onSelectCurrency(code)}
                >
                  {symbolFor(code)} {code}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="cost-row">
          {/* KPI column — one tile per currency. Stacks on desktop, side-by-side on mobile. */}
          <div className="cost-kpis">
            {codes.map(code => (
              <div key={code} className="kpi brand cost-kpi">
                <div className="kpi-label">Total cost ({code})</div>
                <div className="kpi-value">{formatMoney(byCurrency[code].total, code)}</div>
              </div>
            ))}
          </div>

          {/* Chart */}
          <div className="cost-chart">
            {barData.length === 0 ? (
              <p className="muted" style={{ textAlign: 'center', padding: '40px 0', margin: 0 }}>
                No cost data in {cur} for this range.
              </p>
            ) : (
              <ResponsiveContainer width="100%" height={chartHeight}>
                <BarChart data={barData} layout="vertical" margin={{ top: 4, right: 24, left: 16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
                  <XAxis
                    type="number"
                    tickFormatter={v => formatMoney(v, cur)}
                    tick={{ fontSize: 11, fill: 'var(--text-3)' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={140}
                    tick={{ fontSize: 12, fill: 'var(--text-2)' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0].payload;
                      return (
                        <div style={{
                          background: 'var(--surface-2)', border: '1px solid var(--border)',
                          borderRadius: 8, padding: '8px 12px', fontSize: 13, color: 'var(--text)',
                          boxShadow: 'var(--shadow-md)',
                        }}>
                          <div style={{ fontWeight: 600, marginBottom: 4 }}>{d.name}</div>
                          <div><strong>{formatMoney(d.value, cur)}</strong></div>
                        </div>
                      );
                    }}
                  />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {barData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Staff offence breakdown (clinic scope) ─────────────────────────────────────

const OFFENCE_COLOURS = { 1: C_OK, 2: C_WARN, 3: C_DANGER };

function StaffOffencesSection({ offences, clinicName, rangeLabel }) {
  const { staff, first, second, third, unnamed } = offences;
  const max = staff[0]?.count || 1;

  const barData = staff.map(s => ({
    name: s.name,
    count: s.count,
    tier: s.count >= 3 ? '3rd+' : s.count === 2 ? '2nd' : '1st',
    color: s.count >= 3 ? OFFENCE_COLOURS[3] : OFFENCE_COLOURS[s.count] || C_MUTED,
    latest: s.latest,
  }));

  return (
    <div className="reveal delay-4" style={{ marginBottom: 16 }}>
      <div className="panel">
        <div className="panel-head">
          <div className="panel-title">
            Reported staff · {clinicName}{' '}
            <span style={{ color: 'var(--text-3)', fontWeight: 500 }}>· {rangeLabel}</span>
          </div>
        </div>
        <div className="panel-body" style={{ padding: '12px 16px 16px' }}>
          <KpiGrid style={{ marginBottom: 16 }}>
            <StatCard label="1st offence" value={first} colorClass="" />
            <StatCard label="2nd offence" value={second} colorClass="warn" />
            <StatCard label="3rd+ offence" value={third} colorClass="danger" />
            <StatCard label="Staff named" value={staff.length} colorClass="brand" />
          </KpiGrid>

          {staff.length === 0 ? (
            <p className="muted" style={{ textAlign: 'center', padding: '24px 0', margin: 0 }}>
              No staff members recorded in this range.
              {unnamed > 0 ? ` (${unnamed} case${unnamed === 1 ? '' : 's'} without a staff name.)` : ''}
            </p>
          ) : (
            <>
              {barData.length > 0 && barData.length <= 12 && (
                <div style={{ marginBottom: 20 }}>
                  <ResponsiveContainer width="100%" height={Math.max(180, barData.length * 32 + 24)}>
                    <BarChart data={barData} layout="vertical" margin={{ top: 4, right: 24, left: 8, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: 'var(--text-3)' }} axisLine={false} tickLine={false} />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={160}
                        tick={{ fontSize: 12, fill: 'var(--text-2)' }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip
                        content={({ active, payload }) => {
                          if (!active || !payload?.length) return null;
                          const d = payload[0].payload;
                          return (
                            <div style={{
                              background: 'var(--surface-2)', border: '1px solid var(--border)',
                              borderRadius: 8, padding: '8px 12px', fontSize: 13, boxShadow: 'var(--shadow-md)',
                            }}>
                              <div style={{ fontWeight: 600, marginBottom: 4 }}>{d.name}</div>
                              <div>{d.tier} offence · <strong>{d.count}</strong> report{d.count === 1 ? '' : 's'}</div>
                              {d.latest && (
                                <div style={{ color: 'var(--text-3)', marginTop: 4 }}>
                                  Latest: {d.latest.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </div>
                              )}
                            </div>
                          );
                        }}
                      />
                      <Bar dataKey="count" name="Reports" radius={[0, 4, 4, 0]}>
                        {barData.map((d, i) => <Cell key={i} fill={d.color} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              <div>
                {staff.map(s => {
                  const tier = s.count >= 3 ? '3rd+' : s.count === 2 ? '2nd' : '1st';
                  const color = s.count >= 3 ? OFFENCE_COLOURS[3] : OFFENCE_COLOURS[s.count] || C_MUTED;
                  return (
                    <div key={s.name} style={{ marginBottom: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 3, gap: 8 }}>
                        <span style={{ color: 'var(--text)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {s.name}
                          <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color }}>{tier}</span>
                        </span>
                        <span style={{ color: 'var(--text-2)', fontWeight: 600, flexShrink: 0 }}>
                          {s.count}
                          {s.latest && (
                            <span style={{ marginLeft: 8, fontWeight: 400, fontSize: 11, color: 'var(--text-3)' }}>
                              {s.latest.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' })}
                            </span>
                          )}
                        </span>
                      </div>
                      <div style={{ background: 'var(--surface-3)', borderRadius: 4, height: 6, overflow: 'hidden' }}>
                        <div style={{ width: `${Math.round((s.count / max) * 100)}%`, height: '100%', background: color, borderRadius: 4, transition: 'width 0.4s ease' }} />
                      </div>
                    </div>
                  );
                })}
              </div>

              {unnamed > 0 && (
                <p className="muted" style={{ fontSize: 12, marginTop: 12, marginBottom: 0 }}>
                  {unnamed} case{unnamed === 1 ? '' : 's'} in this range {unnamed === 1 ? 'has' : 'have'} no staff member recorded.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Chart panel wrapper ────────────────────────────────────────────────────────
function ChartPanel({ title, children, style }) {
  return (
    <div className="panel" style={style}>
      <div className="panel-head">
        <div className="panel-title">{title}</div>
      </div>
      <div className="panel-body" style={{ padding: '8px 16px 16px' }}>
        {children}
      </div>
    </div>
  );
}

// ── Horizontal mini-bar (used for clinic/treatment lists) ──────────────────────
function MiniBarRow({ name, count, max, color }) {
  const pct = max > 0 ? Math.round((count / max) * 100) : 0;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 3 }}>
        <span style={{ color: 'var(--text)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '75%' }}>{name}</span>
        <span style={{ color: 'var(--text-2)', fontWeight: 600, flexShrink: 0, marginLeft: 8 }}>{count}</span>
      </div>
      <div style={{ background: 'var(--surface-3)', borderRadius: 4, height: 6, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color || C_BRAND, borderRadius: 4, transition: 'width 0.4s ease' }} />
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { config } = useAppConfig();

  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fetchAllTime, setFetchAllTime] = useState(false);

  // Per-case cost aggregates — fetched once for cost-tier roles. Filtered
  // client-side against the timeline range so the brush updates the totals.
  const [costRows, setCostRows] = useState([]);
  const [activeCurrency, setActiveCurrency] = useState(null);

  // How many clinics to show in the bar list (expandable)
  const [clinicLimit, setClinicLimit] = useState(10);
  const [treatmentLimit, setTreatmentLimit] = useState(8);

  // Timeline range filter — default this year (matches API summary window).
  const [range, setRange] = useState(() => defaultDashboardRange());
  const [activePreset, setActivePreset] = useState(DASHBOARD_DEFAULT_PRESET);
  const [brushKey, setBrushKey] = useState(0); // remounts the Brush when a preset sets the window
  const [customExpanded, setCustomExpanded] = useState(false);

  // Geographic scope — all cases, or narrowed to country / region / clinic
  const [scopeMode, setScopeMode]       = useState('all'); // 'all' | 'region' | 'clinic'
  const [scopeCountry, setScopeCountry] = useState('');
  const [scopeRegion, setScopeRegion]   = useState('');
  const [scopeClinic, setScopeClinic]   = useState('');
  const [clinics, setClinics]         = useState([]);
  const [clinicsError, setClinicsError] = useState(null);

  // Width of the timeline chart — used to decide whether the brush traveller
  // labels (e.g. "31 Mar 25") have enough room to render without clipping.
  const brushHostRef = useRef(null);
  const [brushHostWidth, setBrushHostWidth] = useState(0);
  useEffect(() => {
    const el = brushHostRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      setBrushHostWidth(entries[0].contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const loadCases = useCallback(async (cancelled = () => false) => {
    try {
      setLoading(true);
      setError(null);
      const since = fetchAllTime ? undefined : dashboardSinceIso();
      const [data] = await Promise.all([
        getCases({ summary: true, ...(since ? { since } : {}) }),
        getTherapieMapsData()
          .then(d => { if (!cancelled()) setClinics(d.clinics || []); })
          .catch(err => { if (!cancelled()) setClinicsError(err.message); }),
      ]);
      if (!cancelled()) setCases(Array.isArray(data) ? data : []);
    } catch (err) {
      if (!cancelled()) setError(err.message);
    } finally {
      if (!cancelled()) setLoading(false);
    }
  }, [fetchAllTime]);

  useEffect(() => {
    let alive = true;
    loadCases(() => !alive);
    return () => { alive = false; };
  }, [loadCases]);

  const countryOptions = useMemo(
    () => buildCountrySelectOptions(clinics, cases),
    [clinics, cases],
  );

  const regionOptions = useMemo(() => {
    if (!scopeCountry) return [];
    const regions = buildRegionSelectOptions(clinics, cases, scopeCountry);
    return [
      { value: '', label: 'All regions' },
      ...regions,
    ];
  }, [clinics, cases, scopeCountry]);

  const clinicOptions = useMemo(() => {
    const set = new Set();
    const add = name => { if (name) set.add(name); };
    const countryFilter = scopeMode === 'region' && scopeCountry ? scopeCountry : null;
    const regionFilter  = scopeMode === 'region' && scopeRegion ? scopeRegion : null;
    for (const c of clinics) {
      if (countryFilter && c.country !== countryFilter) continue;
      if (regionFilter && c.region !== regionFilter) continue;
      add(c.name);
    }
    for (const c of cases) {
      if (countryFilter && c.country !== countryFilter) continue;
      if (regionFilter && c.region !== regionFilter) continue;
      add(c.clinic);
    }
    return [...set].sort().map(name => {
      const clinic = clinics.find(x => x.name === name);
      return { value: name, label: name, sub: clinic?.region || undefined };
    });
  }, [clinics, cases, scopeMode, scopeCountry, scopeRegion]);

  const scopedCases = useMemo(() => {
    if (scopeMode === 'clinic') {
      return scopeClinic ? cases.filter(c => c.clinic === scopeClinic) : [];
    }
    if (scopeMode === 'region') {
      if (!scopeCountry) return [];
      let out = cases.filter(c => c.country === scopeCountry);
      if (scopeRegion) out = out.filter(c => c.region === scopeRegion);
      return out;
    }
    return cases;
  }, [cases, scopeMode, scopeCountry, scopeRegion, scopeClinic]);

  const scopeAwaitingSelection =
    (scopeMode === 'clinic' && !scopeClinic) ||
    (scopeMode === 'region' && !scopeCountry);

  function applyScopeMode(mode) {
    setScopeMode(mode);
    setRange(defaultDashboardRange());
    setActivePreset(DASHBOARD_DEFAULT_PRESET);
    setFetchAllTime(false);
    setBrushKey(k => k + 1);
    if (mode === 'all') {
      setScopeCountry('');
      setScopeRegion('');
      setScopeClinic('');
    } else if (mode === 'region') {
      setScopeClinic('');
    } else if (mode === 'clinic') {
      setScopeCountry('');
      setScopeRegion('');
    }
  }

  function resetScopeTimeline() {
    setRange(defaultDashboardRange());
    setActivePreset(DASHBOARD_DEFAULT_PRESET);
    setFetchAllTime(false);
    setBrushKey(k => k + 1);
  }

  function onScopeCountryChange(e) {
    setScopeCountry(e.target.value);
    setScopeRegion('');
    resetScopeTimeline();
  }

  function onScopeRegionChange(e) {
    setScopeRegion(e.target.value);
    resetScopeTimeline();
  }

  function onScopeClinicChange(e) {
    setScopeClinic(e.target.value);
    resetScopeTimeline();
  }

  const scopeLabel = scopeMode === 'clinic' && scopeClinic
    ? scopeClinic
    : scopeMode === 'region' && scopeCountry
      ? (() => {
          const countryLabel = COUNTRY_LABELS[scopeCountry] || scopeCountry;
          return scopeRegion ? `${countryLabel} · ${scopeRegion}` : countryLabel;
        })()
      : null;

  const showCountryChart = scopeMode === 'all';
  const showRegionChart  = scopeMode === 'all' || (scopeMode === 'region' && scopeCountry && !scopeRegion);
  const showClinicChart  = scopeMode === 'all' || scopeMode === 'region';
  const showStaffOffences = scopeMode === 'clinic' && !!scopeClinic;

  const applyDashboardView = useCallback((view) => {
    setScopeMode(view.scope);
    if (view.scope === 'region') {
      setScopeCountry(view.country || '');
      setScopeRegion(view.region || '');
      setScopeClinic('');
    } else if (view.scope === 'clinic') {
      setScopeClinic(view.clinic);
      setScopeCountry('');
      setScopeRegion('');
    } else {
      setScopeCountry('');
      setScopeRegion('');
      setScopeClinic('');
    }
    const r = dashboardRangeFromParams(view);
    if (r.range !== undefined) setRange(r.range);
    setActivePreset(r.activePreset);
    setCustomExpanded(r.customExpanded);
    setFetchAllTime(r.activePreset === 'all');
    setBrushKey(k => k + 1);
  }, []);

  useUrlViewSync({
    parse: parseDashboardParams,
    serialize: () => buildDashboardParams({
      scopeMode, scopeCountry, scopeRegion, scopeClinic, activePreset, range,
    }),
    apply: applyDashboardView,
    deps: [scopeMode, scopeCountry, scopeRegion, scopeClinic, activePreset, range],
  });

  // Pull per-case cost aggregates once (cost-tier roles only). The dashboard
  // re-rolls these into byCurrency/byCategory based on the timeline-filtered
  // case set — same client-side filter pattern as the status/severity charts.
  useEffect(() => {
    if (!user?.canSeeCosts) { setCostRows([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const rows = await getCostsByCase();
        if (!cancelled) setCostRows(Array.isArray(rows) ? rows : []);
      } catch (err) {
        console.warn('[Dashboard] cost rollup failed:', err.message);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.canSeeCosts]);

  // ── Timeline filter ─────────────────────────────────────────────────────────
  // Memoize the brush data + filtered cases so referentially-stable arrays
  // don't force Recharts to reset the brush position on unrelated re-renders
  // (e.g. when the cost section's currency toggle fires).
  const bins = useMemo(() => buildTimelineBins(scopedCases), [scopedCases]);
  const [brushStart, brushEnd] = useMemo(() => rangeToIndices(bins, range), [bins, range]);

  const filtered = useMemo(() => {
    if (!range) return scopedCases;
    return scopedCases.filter(c => {
      const d = new Date(c.reportedDate || c.createdDate);
      return !isNaN(d) && d >= range.start && d <= range.end;
    });
  }, [scopedCases, range]);

  function applyPreset(p) {
    if (p.custom) {
      // Toggle the custom row open/closed. Don't touch `range` yet — it
      // applies on the Apply button inside the row.
      if (activePreset === 'custom') {
        setCustomExpanded(prev => !prev);
      } else {
        setActivePreset('custom');
        setCustomExpanded(true);
      }
      return;
    }
    // Any non-custom preset always collapses the custom row.
    setCustomExpanded(false);
    if (p.days) {
      setFetchAllTime(false);
      const end = new Date(); end.setHours(23, 59, 59, 999);
      const start = new Date(end.getTime() - p.days * 864e5); start.setHours(0, 0, 0, 0);
      setRange({ start, end });
    } else if (p.year) {
      setFetchAllTime(false);
      setRange(defaultDashboardRange());
    } else if (p.key === 'all') {
      setFetchAllTime(true);
      setRange(null);
    }
    setActivePreset(p.key);
    setBrushKey(k => k + 1);
  }

  // Apply button inside the custom row — commits the chosen range and collapses.
  function applyCustomRange(fromStr, toStr) {
    if (!fromStr || !toStr) return;
    const start = new Date(`${fromStr}T00:00:00`);
    const end   = new Date(`${toStr}T23:59:59`);
    if (isNaN(start) || isNaN(end) || end < start) return;
    setRange({ start, end });
    setBrushKey(k => k + 1);
    setCustomExpanded(false);
  }

  // Clear button inside the custom row — resets to this year and collapses.
  function clearCustomRange() {
    setRange(defaultDashboardRange());
    setBrushKey(k => k + 1);
    setCustomExpanded(false);
    setActivePreset(DASHBOARD_DEFAULT_PRESET);
    setFetchAllTime(false);
  }

  function onBrush({ startIndex, endIndex }) {
    if (startIndex == null || endIndex == null || !bins.length) return;
    if (startIndex === 0 && endIndex === bins.length - 1) {
      if (fetchAllTime) {
        setRange(null); setActivePreset('all');
      } else {
        setRange(defaultDashboardRange()); setActivePreset(DASHBOARD_DEFAULT_PRESET);
      }
      return;
    }
    setRange({ start: bins[startIndex].start, end: new Date(bins[endIndex].start.getTime() + WEEK_MS - 1) });
    setActivePreset(null);
  }

  const rangeLabel = range
    ? `${fmtDay(range.start)} – ${fmtDay(range.end)}`
    : (fetchAllTime ? 'all time' : 'this year');

  // Visible case ids for client-side cost rollup (matches timeline range).
  const visibleCaseIds = useMemo(
    () => new Set(filtered.map(c => c.id).filter(Number.isFinite)),
    [filtered],
  );

  const staffOffences = useMemo(
    () => (showStaffOffences ? computeStaffOffences(filtered) : null),
    [filtered, showStaffOffences],
  );

  // ── Derived stats (from the timeline-filtered set) ──────────────────────────
  const total    = filtered.length;
  const newCount = filtered.filter(c => c.status === 'New').length;
  const open     = filtered.filter(c => c.status === 'Open').length;
  const pending  = filtered.filter(c => c.status === 'Pending').length;
  const withClaims = filtered.filter(c => c.status === 'With Claims').length;
  const closed   = filtered.filter(c => c.status === 'Closed').length;
  const thisMonth = countThisMonth(filtered);

  // Status pie data — colour pulled from each status's config cssClass (the same
  // class that styles its badge), so chart + badge stay linked.
  const statusColor = v => colorForConfigItem(config.statusValues, 'value', v);
  const statusData = [
    { name: 'New',               value: newCount, color: statusColor('New') },
    { name: 'Open',              value: open,     color: statusColor('Open') },
    { name: 'Pending',           value: pending,  color: statusColor('Pending') },
    { name: 'With Claims', value: withClaims, color: statusColor('With Claims') },
    { name: 'Closed',            value: closed,   color: statusColor('Closed') },
  ].filter(d => d.value > 0);

  // Severity breakdown — colour from each code's config cssClass (its badge fill).
  const severityData = countByField(filtered, c => c.severity).map(d => ({
    ...d,
    color: colorForConfigItem(config.severityCodes, 'value', d.name),
  }));

  // Clinic breakdown (all, shown with limit)
  const clinicData = countBy(filtered, 'clinic');
  const clinicMax  = clinicData[0]?.count || 1;

  // Treatment/department breakdown
  const treatmentData = countBy(filtered, 'treatment');
  const treatmentMax  = treatmentData[0]?.count || 1;

  // Monthly trend (last 18 months) — 3 series: treatment / near miss / clinic incident
  const trendRaw   = countByMonthSeries(filtered);
  const trendData  = trendRaw.slice(-18).map(d => ({ ...d, label: fmtMonth(d.month) }));

  // Region breakdown — grouped under country (GB → IOI → …, regions A–Z within each)
  const regionGroups = countRegionsByCountry(filtered);
  const regionMax = Math.max(
    1,
    ...regionGroups.flatMap(g => g.regions.map(r => r.count)),
  );

  // Country breakdown — values: 'Ireland', 'United Kingdom', 'United States'
  const countryData = countBy(filtered, 'country')
    .filter(d => d.name !== 'Unknown' && d.name !== '')
    .map(d => ({ name: COUNTRY_LABELS[d.name] || d.name, value: d.count, color: COUNTRY_COLOURS[d.name] || C_MUTED }));

  // Year breakdown (for donut) — built directly so we use `value` (not `count`)
  // which matches the recharts Pie dataKey.
  // Token hues first, then darker ink/text shades from the same stylesheet for years 7-10
  const YEAR_COLOURS = [C_BRAND, C_INFO, C_WARN, C_VIOLET, C_DANGER, C_OK, '#0F766E', '#1E40AF', '#92400E', '#5B21B6'];
  const yearData = (() => {
    const map = {};
    for (const c of filtered) {
      const dt = c.reportedDate || c.createdDate;
      if (!dt) continue;
      const y = new Date(dt).getFullYear();
      if (isNaN(y)) continue;
      const key = String(y);
      map[key] = (map[key] || 0) + 1;
    }
    return Object.entries(map)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([name, value], i) => ({ name, value, color: YEAR_COLOURS[i % YEAR_COLOURS.length] }));
  })();

  // Brush traveller labels: hide when the selected window is narrower than the
  // labels themselves can fit. "31 Mar 25" at 11px ≈ 60px wide; reserve ~24px
  // padding either side so the label doesn't kiss the travellers.
  const BRUSH_LABEL_MIN_PX = 110;
  const brushSelectionPx = bins.length > 1
    ? ((brushEnd - brushStart) / (bins.length - 1)) * brushHostWidth
    : 0;
  const showBrushLabels = brushSelectionPx >= BRUSH_LABEL_MIN_PX;

  // ── Loading / error states ──────────────────────────────────────────────────
  const LoadSkel = () => (
    <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-3)' }}>
      Loading analytics…
    </div>
  );

  if (loading) {
    return (
      <>
        <header className="page-head">
          <div>
            <div className="breadcrumb"><span>Overview</span><span>›</span><span>Dashboard</span></div>
            <h1>Dashboard</h1>
            <div className="page-sub">Loading data…</div>
          </div>
        </header>
        <LoadSkel />
      </>
    );
  }

  if (error) {
    return (
      <>
        <header className="page-head">
          <div>
            <div className="breadcrumb"><span>Overview</span><span>›</span><span>Dashboard</span></div>
            <h1>Dashboard</h1>
          </div>
        </header>
        <div style={{ padding: '20px' }}>
          <div className="callout danger">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <div><strong>Could not load data</strong> — {error}</div>
          </div>
        </div>
      </>
    );
  }

  if (scopeAwaitingSelection) {
    return (
      <>
        <header className="page-head reveal">
          <div>
            <div className="breadcrumb">
              <span>Overview</span>
              <span>›</span>
              <span>Dashboard</span>
            </div>
            <div className="eyebrow"><span className="eyebrow-dot" />AE Tracker · Live</div>
            <h1>Dashboard</h1>
            <div className="page-sub">Select a {scopeMode === 'clinic' ? 'clinic' : 'country'} to view scoped analytics</div>
          </div>
          <div className="head-actions">
            <ShareLinkButton />
          </div>
        </header>

        <div className="panel reveal delay-1" style={{ marginBottom: 16 }}>
          <div className="panel-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div className="panel-title">Scope</div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {[
                { key: 'all',    label: 'All' },
                { key: 'region', label: 'By country & region' },
                { key: 'clinic', label: 'By clinic' },
              ].map(opt => (
                <button
                  key={opt.key}
                  className={`btn sm ${scopeMode === opt.key ? 'primary' : 'ghost'}`}
                  onClick={() => applyScopeMode(opt.key)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="panel-body" style={{ padding: '12px 16px 16px', display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
            {scopeMode === 'region' && (
              <>
                <div style={{ flex: '1 1 180px', maxWidth: 220 }}>
                  <label style={{ display: 'block', fontSize: 12, color: 'var(--text-3)', marginBottom: 6 }}>Country</label>
                  <SearchableSelect
                    value={scopeCountry}
                    onChange={onScopeCountryChange}
                    options={countryOptions}
                    placeholder="Select country…"
                    noOptionsLabel="No countries found"
                  />
                </div>
                <div style={{ flex: '1 1 240px', maxWidth: 360 }}>
                  <label style={{ display: 'block', fontSize: 12, color: 'var(--text-3)', marginBottom: 6 }}>Region <span style={{ fontWeight: 400 }}>(optional)</span></label>
                  <SearchableSelect
                    value={scopeRegion}
                    onChange={onScopeRegionChange}
                    options={regionOptions}
                    placeholder={scopeCountry ? 'All regions' : 'Select country first'}
                    disabled={!scopeCountry}
                    noOptionsLabel="No regions found"
                  />
                </div>
              </>
            )}
            {scopeMode === 'clinic' && (
              <div style={{ flex: '1 1 240px', maxWidth: 360 }}>
                <label style={{ display: 'block', fontSize: 12, color: 'var(--text-3)', marginBottom: 6 }}>Clinic</label>
                <SearchableSelect
                  value={scopeClinic}
                  onChange={onScopeClinicChange}
                  options={clinicOptions}
                  placeholder={clinics.length === 0 && !clinicsError ? 'Loading clinics…' : 'Select clinic…'}
                  disabled={clinics.length === 0 && !clinicsError && clinicOptions.length === 0}
                  noOptionsLabel="No clinics found"
                />
              </div>
            )}
          </div>
        </div>

        <div className="panel reveal delay-2">
          <div className="panel-body" style={{ padding: '48px 24px', textAlign: 'center' }}>
            <p className="muted" style={{ margin: 0, fontSize: 15 }}>
              Choose a {scopeMode === 'clinic' ? 'clinic' : 'country'} above to load timeline, KPIs, and charts for that scope.
            </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* ───────── Page head ───────── */}
      <header className="page-head reveal">
        <div>
          <div className="breadcrumb">
            <span>Overview</span>
            <span>›</span>
            <span>Dashboard</span>
          </div>
          <div className="eyebrow"><span className="eyebrow-dot" />AE Tracker · Live</div>
          <h1>Dashboard</h1>
          <div className="page-sub">
            {total.toLocaleString()} adverse events
            {scopeLabel ? ` · ${scopeLabel}` : ''}
            {' · '}{rangeLabel}
          </div>
        </div>
        <div className="head-actions">
          <ShareLinkButton />
          <button className="btn ghost" onClick={() => navigate('/cases')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
            </svg>
            View All Cases
          </button>
        </div>
      </header>

      {/* ───────── Scope filter (region / clinic) ───────── */}
      <div className="panel reveal delay-1" style={{ marginBottom: 16 }}>
        <div className="panel-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div className="panel-title">Scope</div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {[
              { key: 'all',    label: 'All' },
              { key: 'region', label: 'By country & region' },
              { key: 'clinic', label: 'By clinic' },
            ].map(opt => (
              <button
                key={opt.key}
                className={`btn sm ${scopeMode === opt.key ? 'primary' : 'ghost'}`}
                onClick={() => applyScopeMode(opt.key)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        {scopeMode !== 'all' && (
          <div className="panel-body" style={{ padding: '12px 16px 16px', display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
            {scopeMode === 'region' && (
              <>
                <div style={{ flex: '1 1 180px', maxWidth: 220 }}>
                  <label style={{ display: 'block', fontSize: 12, color: 'var(--text-3)', marginBottom: 6 }}>Country</label>
                  <SearchableSelect
                    value={scopeCountry}
                    onChange={onScopeCountryChange}
                    options={countryOptions}
                    placeholder="Select country…"
                    noOptionsLabel="No countries found"
                  />
                </div>
                <div style={{ flex: '1 1 240px', maxWidth: 360 }}>
                  <label style={{ display: 'block', fontSize: 12, color: 'var(--text-3)', marginBottom: 6 }}>Region <span style={{ fontWeight: 400 }}>(optional)</span></label>
                  <SearchableSelect
                    value={scopeRegion}
                    onChange={onScopeRegionChange}
                    options={regionOptions}
                    placeholder={scopeCountry ? 'All regions' : 'Select country first'}
                    disabled={!scopeCountry}
                    noOptionsLabel="No regions found"
                  />
                </div>
              </>
            )}
            {scopeMode === 'clinic' && (
              <div style={{ flex: '1 1 240px', maxWidth: 360 }}>
                <label style={{ display: 'block', fontSize: 12, color: 'var(--text-3)', marginBottom: 6 }}>Clinic</label>
                <SearchableSelect
                  value={scopeClinic}
                  onChange={onScopeClinicChange}
                  options={clinicOptions}
                  placeholder={clinics.length === 0 && !clinicsError ? 'Loading clinics…' : 'Select clinic…'}
                  disabled={clinics.length === 0 && !clinicsError && clinicOptions.length === 0}
                  noOptionsLabel="No clinics found"
                />
              </div>
            )}
            {clinicsError && (
              <p className="muted" style={{ fontSize: 12, margin: 0 }}>
                Clinic list unavailable — using case data only.
              </p>
            )}
            {scopeMode === 'region' && scopeCountry && (
              <p className="muted" style={{ fontSize: 12, margin: 0, flex: '1 1 100%' }}>
                Showing {scopedCases.length.toLocaleString()} case{scopedCases.length === 1 ? '' : 's'}
                {scopeRegion
                  ? ` in ${scopeRegion} (${COUNTRY_LABELS[scopeCountry] || scopeCountry})`
                  : ` in ${COUNTRY_LABELS[scopeCountry] || scopeCountry}`}
                {scopeRegion ? '. Country and region charts are hidden.' : '. Country chart is hidden; region breakdown shows areas within this country.'}
              </p>
            )}
            {scopeMode === 'clinic' && scopeClinic && (
              <p className="muted" style={{ fontSize: 12, margin: 0, flex: '1 1 100%' }}>
                Showing {scopedCases.length.toLocaleString()} case{scopedCases.length === 1 ? '' : 's'} for {scopeClinic}. Geographic breakdowns are hidden; staff offence stats appear below the timeline.
              </p>
            )}
          </div>
        )}
      </div>

      {/* ───────── Timeline range selector ───────── */}
      {bins.length > 1 && (
        <div className="panel reveal delay-1" style={{ marginBottom: '16px' }}>
          <div className="panel-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <div className="panel-title">
              Timeline{' '}
              <span style={{ color: 'var(--text-3)', fontWeight: 500 }}>
                · {scopeLabel || 'all clinics'}
                {' · '}{rangeLabel} · {total.toLocaleString()} cases
              </span>
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              {PRESETS.map(p => (
                <button
                  key={p.key}
                  className={`btn sm ${activePreset === p.key ? 'primary' : 'ghost'}`}
                  onClick={() => applyPreset(p)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Custom range — date inputs + Apply / Clear. Collapses on Apply. */}
          {activePreset === 'custom' && customExpanded && (
            <CustomRangeRow
              range={range}
              bins={bins}
              onApply={applyCustomRange}
              onClear={clearCustomRange}
            />
          )}

          <div className="panel-body" style={{ padding: '4px 16px 10px' }} ref={brushHostRef}>
            <ResponsiveContainer width="100%" height={104}>
              <BarChart data={bins} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fill: 'var(--text-3)' }}
                  minTickGap={48}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="count" name="Cases" fill={C_BRAND} fillOpacity={0.55} radius={[4, 4, 0, 0]} />
                <Brush
                  key={brushKey}
                  dataKey="label"
                  height={20}
                  travellerWidth={14}
                  stroke="transparent"
                  startIndex={brushStart}
                  endIndex={brushEnd}
                  onDragEnd={onBrush}
                  tickFormatter={v => (showBrushLabels ? v : '')}
                  traveller={BrushTraveller}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ───────── KPI row ───────── */}
      <KpiGrid className="reveal delay-1">
        <StatCard label="Total" value={total.toLocaleString()} colorClass="" />
        <StatCard
          label="New" value={newCount}
          colorClass="warn"
          onClick={() => navigate('/cases')}
        />
        <StatCard
          label="Open" value={open}
          colorClass="info"
          onClick={() => navigate('/cases')}
        />
        <StatCard
          label="Pending" value={pending}
          colorClass="brand"
          onClick={() => navigate('/cases')}
        />
        <StatCard
          label="With Claims" value={withClaims}
          colorClass="danger"
          onClick={() => navigate('/cases')}
        />
        <StatCard
          label="Closed" value={closed}
          colorClass=""
          onClick={() => navigate('/cases')}
        />
        <StatCard
          label="This Month" value={thisMonth}
          colorClass=""
        />
      </KpiGrid>

      {/* ───────── Staff offences (clinic scope) ───────── */}
      {showStaffOffences && staffOffences && (
        <StaffOffencesSection
          offences={staffOffences}
          clinicName={scopeClinic}
          rangeLabel={rangeLabel}
        />
      )}

      {/* ───────── Costs (cost-tier roles only) ───────── */}
      {user?.canSeeCosts && (
        <CostSection
          costRows={costRows}
          visibleCaseIds={visibleCaseIds}
          categories={config.costCategories || []}
          currencies={config.currencies     || []}
          activeCurrency={activeCurrency}
          onSelectCurrency={setActiveCurrency}
        />
      )}

      {/* ───────── Row 1: Trend (full width) ───────── */}
      <div className="reveal delay-2" style={{ marginBottom: '16px' }}>
        <ChartPanel title="Cases over time (monthly)">
          {trendData.length === 0 ? (
            <p className="muted" style={{ textAlign: 'center', padding: '40px 0' }}>No date data available.</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={trendData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--text-3)' }} interval={1} />
                <YAxis tick={{ fontSize: 11, fill: 'var(--text-3)' }} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Legend
                  iconType="circle"
                  iconSize={8}
                  formatter={(value) => <span style={{ fontSize: 11, color: 'var(--text-2)' }}>{value}</span>}
                />
                <Line type="monotone" dataKey="treatment"      name="Treatment"       stroke={C_BRAND}  strokeWidth={2} dot={false} activeDot={{ r: 4, fill: C_BRAND }}  />
                <Line type="monotone" dataKey="nearMiss"       name="Near miss"       stroke={C_WARN}   strokeWidth={2} dot={false} activeDot={{ r: 4, fill: C_WARN }}   />
                <Line type="monotone" dataKey="clinicIncident" name="Clinic incident" stroke={C_VIOLET} strokeWidth={2} dot={false} activeDot={{ r: 4, fill: C_VIOLET }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartPanel>
      </div>

      {/* ───────── Row 2: Status · Year · Severity (1/4 · 1/4 · 1/2) ───────── */}
      <div className="row analytics-2 reveal delay-3" style={{ marginBottom: '16px', alignItems: 'stretch' }}>

        {/* By status donut */}
        <ChartPanel title="By status">
          {statusData.length === 0 ? (
            <p className="muted" style={{ textAlign: 'center', padding: '40px 0' }}>No data.</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="40%"
                  innerRadius={50}
                  outerRadius={75}
                  paddingAngle={3}
                  dataKey="value"
                  stroke="none"
                >
                  {statusData.map((entry, index) => (
                    <Cell key={index} fill={entry.color} stroke="none" />
                  ))}
                </Pie>
                <Tooltip content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0].payload;
                  return (
                    <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', fontSize: 13, boxShadow: 'var(--shadow-md)' }}>
                      <strong>{d.name}</strong>: {d.value}
                    </div>
                  );
                }} />
                <Legend
                  iconType="circle"
                  iconSize={8}
                  payload={statusData.map(d => ({ value: d.name, type: 'circle', color: d.color }))}
                  formatter={(value) => <span style={{ fontSize: 11, color: 'var(--text-2)' }}>{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartPanel>

        {/* By year donut */}
        <ChartPanel title="By year">
          {yearData.length === 0 ? (
            <p className="muted" style={{ textAlign: 'center', padding: '40px 0' }}>No year data.</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={yearData}
                  cx="50%"
                  cy="40%"
                  innerRadius={50}
                  outerRadius={75}
                  paddingAngle={3}
                  dataKey="value"
                  stroke="none"
                >
                  {yearData.map((entry, index) => (
                    <Cell key={index} fill={entry.color} stroke="none" />
                  ))}
                </Pie>
                <Tooltip content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0].payload;
                  return (
                    <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', fontSize: 13, boxShadow: 'var(--shadow-md)' }}>
                      <strong>{d.name}</strong>: {d.value}
                    </div>
                  );
                }} />
                <Legend
                  iconType="circle"
                  iconSize={8}
                  payload={yearData.map(d => ({ value: d.name, type: 'circle', color: d.color }))}
                  formatter={(value) => <span style={{ fontSize: 11, color: 'var(--text-2)' }}>{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartPanel>

        {/* Severity breakdown */}
        <ChartPanel title="By severity code">
          {severityData.length === 0 ? (
            <p className="muted" style={{ textAlign: 'center', padding: '40px 0' }}>No severity data.</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={severityData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: 'var(--text-3)' }} />
                <YAxis tick={{ fontSize: 11, fill: 'var(--text-3)' }} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="count" name="Cases" radius={[4, 4, 0, 0]}>
                  {severityData.map((entry, index) => (
                    <Cell key={index} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartPanel>
      </div>

      {/* ───────── Row 3: Treatment (full width) ───────── */}
      <div className="reveal delay-4" style={{ marginBottom: '16px' }}>
        <ChartPanel title="By treatment / department">
          {treatmentData.length === 0 ? (
            <p className="muted" style={{ textAlign: 'center', padding: '40px 0' }}>No treatment data.</p>
          ) : (
            <div className="treatment-cols">
              {treatmentData.slice(0, treatmentLimit).map((d) => (
                <MiniBarRow key={d.name} name={d.name} count={d.count} max={treatmentMax} color={C_INFO} />
              ))}
              {treatmentData.length > treatmentLimit && (
                <div style={{ columnSpan: 'all', marginTop: 8 }}>
                  <button className="btn ghost sm" onClick={() => setTreatmentLimit(l => l + 8)}>
                    Show more ({treatmentData.length - treatmentLimit} remaining)
                  </button>
                </div>
              )}
            </div>
          )}
        </ChartPanel>
      </div>

      {/* ───────── Row 4: Country · Region · Clinic (scope-aware) ───────── */}
      {(showCountryChart || showRegionChart || showClinicChart) && (
      <div
        className={`row ${showCountryChart && showRegionChart && showClinicChart ? 'split-3' : showClinicChart ? '' : 'analytics-2'} reveal delay-5`}
        style={{ marginBottom: '24px', alignItems: 'stretch', display: showClinicChart && !showCountryChart && !showRegionChart ? 'block' : undefined }}
      >

        {/* Country breakdown */}
        {showCountryChart && (
        <ChartPanel title="By country">
          {countryData.length === 0 ? (
            <p className="muted" style={{ textAlign: 'center', padding: '40px 0' }}>No country data.</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={countryData}
                  cx="50%"
                  cy="40%"
                  innerRadius={50}
                  outerRadius={75}
                  paddingAngle={3}
                  dataKey="value"
                  stroke="none"
                >
                  {countryData.map((entry, index) => (
                    <Cell key={index} fill={entry.color} stroke="none" />
                  ))}
                </Pie>
                <Tooltip content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0].payload;
                  return (
                    <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', fontSize: 13, boxShadow: 'var(--shadow-md)' }}>
                      <strong>{d.name}</strong>: {d.value}
                    </div>
                  );
                }} />
                <Legend
                  iconType="circle"
                  iconSize={8}
                  payload={countryData.map(d => ({ value: d.name, type: 'circle', color: d.color }))}
                  formatter={(value) => <span style={{ fontSize: 11, color: 'var(--text-2)' }}>{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartPanel>
        )}

        {/* Region breakdown (grouped by country) */}
        {showRegionChart && (
        <ChartPanel title="By country & region">
          {regionGroups.length === 0 ? (
            <p className="muted" style={{ textAlign: 'center', padding: '40px 0' }}>No region data.</p>
          ) : (
            <div>
              {regionGroups.map((group, gi) => (
                <div key={group.country} style={{ marginBottom: gi < regionGroups.length - 1 ? 18 : 0 }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    fontSize: 11,
                    fontWeight: 600,
                    color: 'var(--text-3)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    marginBottom: 8,
                    paddingBottom: 4,
                    borderBottom: '1px solid var(--border)',
                  }}>
                    <span>{group.countryLabel}</span>
                    <span style={{ fontWeight: 500 }}>{group.total}</span>
                  </div>
                  {group.regions.map(d => (
                    <MiniBarRow
                      key={`${group.country}-${d.name}`}
                      name={d.name}
                      count={d.count}
                      max={regionMax}
                      color={group.color}
                    />
                  ))}
                </div>
              ))}
            </div>
          )}
        </ChartPanel>
        )}

        {/* Clinic breakdown */}
        {showClinicChart && (
        <ChartPanel title={scopeMode === 'region' ? 'Clinics in region' : 'By clinic'}>
          {clinicData.length === 0 ? (
            <p className="muted" style={{ textAlign: 'center', padding: '40px 0' }}>No clinic data.</p>
          ) : (
            <div>
              {clinicData.slice(0, clinicLimit).map((d) => (
                <MiniBarRow key={d.name} name={d.name} count={d.count} max={clinicMax} color={C_BRAND} />
              ))}
              {clinicData.length > clinicLimit && (
                <button className="btn ghost sm" style={{ marginTop: 8 }} onClick={() => setClinicLimit(l => l + 10)}>
                  Show more ({clinicData.length - clinicLimit} remaining)
                </button>
              )}
            </div>
          )}
        </ChartPanel>
        )}
      </div>
      )}
    </>
  );
}
